/**
 * BuyCreditsModal — one-time Razorpay checkout for a notification-credit
 * top-up (WhatsApp / SMS sends -- email sends are free and unmetered).
 *
 * Structurally the same as the plan-upgrade one-time order shape (order_id +
 * amount_paise + razorpay_key_id, not a subscription_id) — mirrors
 * FinanceBilling.tsx's handleSubscribe Razorpay-checkout pattern, adapted for
 * a one-time order instead of a subscription.
 *
 * POST /credits/order is ROLE_ADMIN only on the backend, so a non-admin who
 * triggers this (e.g. via a 402 on a send, or the header wallet widget) sees
 * a "contact your admin" notice instead of the purchase form — same
 * admin/non-admin branching UpgradePromptModal uses for feature gates.
 */
import React, { useState } from "react";
import { toast } from "react-toastify";

import { useRole } from "../../hooks/useRole";
import { CreditsAPI } from "../../services/credits.service";
import { CUSTOM_CREDITS_MIN, CUSTOM_CREDITS_STEP, COMMS_CREDIT_COST_INR } from "../../constants";

interface Props {
  onClose: () => void;
  /** Called right after a successful checkout so the caller's own
   *  useWalletBalance() instance reflects the purchase immediately, without
   *  waiting on the AppSync push or the next poll tick. */
  onPurchased?: () => void;
}

export const BuyCreditsModal: React.FC<Props> = ({ onClose, onPurchased }) => {
  const { isAdmin, isLoaded } = useRole();
  const [units, setUnits] = useState(0);
  const [placing, setPlacing] = useState(false);

  const amountInr = CUSTOM_CREDITS_MIN + units * CUSTOM_CREDITS_STEP;
  const approxSends = Math.floor(amountInr / COMMS_CREDIT_COST_INR);

  const handleBuy = async () => {
    setPlacing(true);
    try {
      const order = await CreditsAPI.createTopupOrder(units);

      if ((window as any).Razorpay) {
        const rzp = new (window as any).Razorpay({
          key: order.razorpay_key_id,
          order_id: order.order_id,
          amount: order.amount_paise,
          currency: "INR",
          name: "Kaagazy EMR",
          description: "Notification credit top-up",
          handler: () => {
            toast.success("Credits purchased — your balance will update shortly.");
            onPurchased?.();
            onClose();
          },
          modal: {
            ondismiss: () => {
              setPlacing(false);
              toast.info("Payment was not completed. You can try again anytime.");
            },
          },
        });
        rzp.open();
      } else {
        toast.error("Payment could not be started. Please refresh and try again.");
        setPlacing(false);
      }
    } catch (err: any) {
      const detail = err?.body?.detail ?? err?.detail;
      toast.error(typeof detail === "string" ? detail : err?.message || "Failed to start checkout");
      setPlacing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[420px] p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Buy notification credits</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Used for WhatsApp &amp; SMS sends to patients. Email sends are free.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!isLoaded ? (
          <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
        ) : !isAdmin ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            Only a clinic admin can buy notification credits. Please ask your admin to top up
            from Settings → Finance &amp; Billing.
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Credits</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setUnits((u) => Math.max(0, u - 1))}
                    disabled={units === 0}
                    aria-label="Decrease credits"
                    className="w-8 h-8 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    −
                  </button>
                  <span className="text-xl font-bold text-slate-900 min-w-[120px] text-center">
                    {approxSends.toLocaleString("en-IN")} credits
                  </span>
                  <button
                    type="button"
                    onClick={() => setUnits((u) => u + 1)}
                    aria-label="Increase credits"
                    className="w-8 h-8 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={handleBuy}
              disabled={placing}
              className="w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)" }}
            >
              {placing ? "Processing…" : `Buy for ₹${amountInr.toLocaleString("en-IN")}`}
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-2">
              Billed once via Razorpay · exclusive of GST
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyCreditsModal;
